import { Asset } from '@/types/models/assets.types';
import React, { useRef, useState, useEffect } from 'react';
import { AssetActionsBar, FaultyComponents } from '../../shared';

interface FooterSectionProps {
  asset: Asset;
  isRequireAttention?: boolean;
  className?: string;
}

const FooterSectionComponent = ({ asset, isRequireAttention, className }: FooterSectionProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [maxBadges, setMaxBadges] = useState(2);
  const resizeTimeout = useRef<NodeJS.Timeout | null>(null);

  if (!isRequireAttention && asset?.faulty_components && asset?.faulty_components?.length > 0) return null;
  const faulty_components = asset.faulty_components || [];

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (faulty_components.length === 0) return;

    const updateMaxBadges = () => {
      const containerWidth = container.clientWidth;
      const faultyItems: number[] =
        (asset?.faulty_components &&
          asset.faulty_components?.map((el: any) => {
            const name = el.unit_ui_name ? `${el.unit_ui_name} - ${el.component_ui_name}` : el.component_ui_name;
            return name.length * 10;
          })) ||
        [];
      // Decide the max badges based on the container width and badge width (estimate)
      const badgeWidth = Math.min(Math.floor(faultyItems.reduce((a, b) => a + b, 0) / faultyItems?.length), 220); // Estimate width for each badge
      // const badgeWidth = Math.max(...faultyItems, 180); // Estimate width for each badge
      const maxFitBadges = Math.floor(containerWidth / badgeWidth);

      // If the container can fit more badges, update maxBadges accordingly
      setMaxBadges((prevMaxBadges) => {
        // If the container can fit more badges and we haven't already reached the max
        if (maxFitBadges > prevMaxBadges && maxBadges < faulty_components.length) {
          return Math.min(maxFitBadges, faulty_components.length); // Ensure it doesn't exceed total components
        } else if (maxFitBadges < prevMaxBadges) {
          return maxFitBadges; // Reduce the badges if there's not enough space
        }
        return prevMaxBadges;
      });
    };

    // Run on mount to initialize
    updateMaxBadges();

    // Attach resize observer
    const resizeObserver = new ResizeObserver(() => {
      if (resizeTimeout.current) clearTimeout(resizeTimeout.current);

      resizeTimeout.current = setTimeout(updateMaxBadges, 15); // Debounce resize event
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      if (resizeTimeout.current) clearTimeout(resizeTimeout.current);
    };
  }, [maxBadges, faulty_components]);

  return (
    <div
      className={`flex justify-between items-center ${!asset.not_monitored || 'grayscale'} w-full `}
      ref={containerRef}
    >
      <FaultyComponents
        faulty_components={faulty_components}
        asset_id={asset.asset_id}
        maxBadges={maxBadges}
        className={className}
      />
      {/* <div className="flex items-center justify-end gap-2 h-full">
        <AssetActionsBar asset={asset} />
      </div> */}
    </div>
  );
};

export const FooterSection = React.memo(FooterSectionComponent);
